import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaTasks, FaUser, FaCalendarAlt, FaFlag, FaBuilding, FaProjectDiagram, FaFileAlt, FaFileInvoiceDollar, FaLock, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import CrmTaskService, {
  CrmTask,
  CrmTaskPriority,
  CRM_TASK_PRIORITY_OPTIONS,
  CreateCrmTaskPayload,
} from '../services/CrmTaskService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';
import ProjectService, { Project } from '../../contacts/services/ProjectService';
import OfferService, { Offer } from '../services/OfferService';
import InvoiceService, { Invoice } from '../services/InvoiceService';
import EmployeeService from '../../employees/services/EmployeeService';
import { Employee } from '../../../hooks/useEmployeeData';
import TaskCommentsSection from './TaskCommentsSection';

interface CrmTaskModalProps {
  isOpen: boolean;
  task?: CrmTask | null;
  partnerId?: number | null;
  partnerName?: string | null;
  projectId?: number | null;
  offerId?: number | null;
  invoiceId?: number | null;
  paymentMilestoneId?: number;
  paymentMilestoneLabel?: string;
  onClose: () => void;
  onSaved: (task: CrmTask) => void;
}

export default function CrmTaskModal({
  isOpen,
  task,
  partnerId: initialPartnerId,
  partnerName: initialPartnerName,
  projectId,
  offerId,
  invoiceId,
  paymentMilestoneId,
  paymentMilestoneLabel,
  onClose,
  onSaved,
}: CrmTaskModalProps): React.JSX.Element | null {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<CrmTaskPriority>('NORMAL');
  const [assignedTo, setAssignedTo] = useState<number | null>(null);
  const [collaborators, setCollaborators] = useState<number[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [partners, setPartners] = useState<BusinessPartner[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  const isEditMode = !!task;
  const isPartnerLocked = !!initialPartnerId;

  const getPartnerDisplayName = (partner: BusinessPartner): string => {
    if (partner.is_company && partner.company_name) {
      return partner.company_name;
    }
    return `${partner.first_name || ''} ${partner.last_name || ''}`.trim() || `Partner #${partner.id}`;
  };

  const loadRelatedData = useCallback(async (partnerId: number | null) => {
    setLoadingRelated(true);
    try {
      const [employeesData, partnersData] = await Promise.all([
        EmployeeService.fetchEmployeesByStatus(['ACTIVE']),
        BusinessPartnerService.list({ kind: 'CUSTOMER' }),
      ]);
      setEmployees(employeesData);
      setPartners(partnersData);

      if (partnerId) {
        const [projectsData, offersData, invoicesData] = await Promise.all([
          ProjectService.list({ partner_id: partnerId }),
          OfferService.list({ partner_id: partnerId }),
          InvoiceService.list({ partner_id: partnerId }),
        ]);
        setProjects(projectsData);
        setOffers(offersData);
        setInvoices(invoicesData);
      } else {
        setProjects([]);
        setOffers([]);
        setInvoices([]);
      }
    } catch (err) {
      console.error('Failed to load related data:', err);
    } finally {
      setLoadingRelated(false);
    }
  }, []);

  const handlePartnerChange = async (newPartnerId: number | null) => {
    setSelectedPartnerId(newPartnerId);
    setSelectedProjectId(null);
    setSelectedOfferId(null);
    setSelectedInvoiceId(null);

    if (newPartnerId) {
      try {
        const [projectsData, offersData, invoicesData] = await Promise.all([
          ProjectService.list({ partner_id: newPartnerId }),
          OfferService.list({ partner_id: newPartnerId }),
          InvoiceService.list({ partner_id: newPartnerId }),
        ]);
        setProjects(projectsData);
        setOffers(offersData);
        setInvoices(invoicesData);
      } catch (err) {
        console.error('Failed to load partner-related data:', err);
      }
    } else {
      setProjects([]);
      setOffers([]);
      setInvoices([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const effectivePartnerId = task?.partner ?? initialPartnerId ?? null;

      if (task) {
        setTitle(task.title);
        setDescription(task.description || '');
        setDueDate(task.due_date || '');
        setPriority(task.priority);
        setAssignedTo(task.assigned_to);
        setCollaborators((task as any).collaborators || []);
        setSelectedPartnerId(task.partner);
        setSelectedProjectId(task.project);
        setSelectedOfferId(task.offer);
        setSelectedInvoiceId(task.invoice);
      } else {
        setTitle('');
        setDescription('');
        setDueDate(CrmTaskService.getDefaultDueDate());
        setPriority('NORMAL');
        setAssignedTo(null);
        setCollaborators([]);
        setSelectedPartnerId(initialPartnerId || null);
        setSelectedProjectId(projectId || null);
        setSelectedOfferId(offerId || null);
        setSelectedInvoiceId(invoiceId || null);
      }

      loadRelatedData(effectivePartnerId);
    }
  }, [isOpen, task, initialPartnerId, projectId, offerId, invoiceId, loadRelatedData]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!dueDate) {
      toast.error('Due date is required');
      return;
    }

    setSaving(true);
    try {
      const payload: CreateCrmTaskPayload = {
        title: title.trim(),
        description: description.trim() || undefined,
        due_date: dueDate,
        priority,
        assigned_to: assignedTo,
        partner: selectedPartnerId,
        project: selectedProjectId,
        offer: selectedOfferId,
        invoice: selectedInvoiceId,
        payment_milestone: paymentMilestoneId ?? null,
      };

      let savedTask: CrmTask;
      if (isEditMode && task) {
        savedTask = await CrmTaskService.update(task.id, payload);
        toast.success('Task updated');
      } else {
        savedTask = await CrmTaskService.create(payload);
        toast.success('Task created');
      }

      onSaved(savedTask);
    } catch (err: any) {
      console.error('Failed to save task:', err);
      const message = err?.response?.data?.detail || 'Failed to save task';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const getPriorityColor = (p: CrmTaskPriority) => {
    switch (p) {
      case 'HIGH':
        return { bg: colors.dangerLight, border: colors.danger, text: colors.danger };
      case 'NORMAL':
        return { bg: colors.infoLight, border: colors.info, text: colors.navy };
      case 'LOW':
        return { bg: colors.bgMuted, border: colors.border, text: colors.textBody };
      default:
        return { bg: colors.bgMuted, border: colors.border, text: colors.textBody };
    }
  };

  const toggleCollaborator = (empId: number) => {
    setCollaborators((prev) =>
      prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]
    );
  };

  const getEmployeeName = (empId: number): string => {
    const emp = employees.find((e) => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : '';
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '95%',
          maxWidth: '900px',
          height: '90vh',
          maxHeight: '800px',
          overflow: 'hidden',
          boxShadow: shadows.elevated,
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 20px',
            borderBottom: `1px solid ${colors.border}`,
            background: colors.bgMuted,
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaTasks size={16} color={colors.navy} />
            <span style={{ fontSize: '13px', color: colors.textLight }}>
              {isEditMode ? 'Edit Task' : 'New Task'}
            </span>
            {isEditMode && (
              <span style={{ fontSize: '12px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FaLock size={10} />
                This task is private to you.
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              color: colors.textMuted,
              borderRadius: '10px',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Main Content - Two Column Layout */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left Column - Main Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px', borderRight: `1px solid ${colors.border}` }}>
            {/* Title - Large input at top */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Task name"
              style={{
                width: '100%',
                padding: '8px 0',
                border: 'none',
                fontSize: '24px',
                fontWeight: 700,
                color: colors.navy,
                outline: 'none',
                marginBottom: '24px',
              }}
              autoFocus
            />

            {/* Description */}
            <div style={{ marginBottom: '24px' }}>
              <div style={sectionLabelStyle}>Description</div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add more details about this task..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  minHeight: '100px',
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                }}
              />
            </div>

            {/* Comments Section - Prominent in edit mode */}
            {isEditMode && task && (
              <div>
                <div style={sectionLabelStyle}>Comments</div>
                <TaskCommentsSection taskId={task.id} />
              </div>
            )}
          </div>

          {/* Right Column - Meta & Links */}
          <div style={{ width: '320px', overflow: 'auto', padding: '24px', background: colors.bgMuted, flexShrink: 0 }}>
            {/* Assignee */}
            <div style={{ marginBottom: '20px' }}>
              <div style={fieldLabelStyle}>
                <FaUser size={12} color={colors.textMuted} />
                Assignee
              </div>
              <select
                value={assignedTo || ''}
                onChange={(e) => setAssignedTo(e.target.value ? Number(e.target.value) : null)}
                disabled={loadingRelated}
                style={fieldSelectStyle}
              >
                <option value="">Unassigned</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Due Date */}
            <div style={{ marginBottom: '20px' }}>
              <div style={fieldLabelStyle}>
                <FaCalendarAlt size={12} color={colors.textMuted} />
                Due date
              </div>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={fieldSelectStyle}
              />
            </div>

            {/* Priority */}
            <div style={{ marginBottom: '20px' }}>
              <div style={fieldLabelStyle}>
                <FaFlag size={12} color={colors.textMuted} />
                Priority
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {CRM_TASK_PRIORITY_OPTIONS.map((opt) => {
                  const isSelected = priority === opt.value;
                  const priorityColors = getPriorityColor(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setPriority(opt.value)}
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        border: `1px solid ${isSelected ? priorityColors.border : colors.border}`,
                        borderRadius: '10px',
                        background: isSelected ? priorityColors.bg : colors.bgCard,
                        color: isSelected ? priorityColors.text : colors.textMuted,
                        fontSize: '13px',
                        fontWeight: isSelected ? 600 : 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Projects/Links Section */}
            <div style={{ marginBottom: '20px' }}>
              <div style={fieldLabelStyle}>
                <FaProjectDiagram size={12} color={colors.textMuted} />
                Projects
              </div>
              <div style={{ fontSize: '13px', color: colors.textLight, padding: '8px 0' }}>
                {selectedProjectId ? projects.find(p => p.id === selectedProjectId)?.name : 'No project'}
              </div>
            </div>

            {/* Dependencies placeholder */}
            <div style={{ marginBottom: '20px' }}>
              <div style={fieldLabelStyle}>Dependencies</div>
              <div style={{ fontSize: '13px', color: colors.textLight, padding: '8px 0' }}>—</div>
            </div>

            <div style={{ borderTop: `1px solid ${colors.border}`, margin: '16px 0' }} />

            {/* Customer */}
            <div style={{ marginBottom: '16px' }}>
              <div style={fieldLabelStyle}>
                <FaBuilding size={12} color={colors.textMuted} />
                Customer
              </div>
              {isPartnerLocked ? (
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.navy, padding: '8px 0' }}>
                  {initialPartnerName || 'Customer'}
                </div>
              ) : (
                <select
                  value={selectedPartnerId || ''}
                  onChange={(e) => handlePartnerChange(e.target.value ? Number(e.target.value) : null)}
                  disabled={loadingRelated}
                  style={fieldSelectStyle}
                >
                  <option value="">None</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>
                      {getPartnerDisplayName(p)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Project Select */}
            <div style={{ marginBottom: '16px' }}>
              <div style={fieldLabelStyle}>
                <FaProjectDiagram size={12} color={colors.textMuted} />
                Project
              </div>
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value ? Number(e.target.value) : null)}
                disabled={loadingRelated || !selectedPartnerId}
                style={fieldSelectStyle}
              >
                <option value="">{selectedPartnerId ? 'None' : 'Select customer first'}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Offer */}
            <div style={{ marginBottom: '16px' }}>
              <div style={fieldLabelStyle}>
                <FaFileAlt size={12} color={colors.textMuted} />
                Offer
              </div>
              <select
                value={selectedOfferId || ''}
                onChange={(e) => setSelectedOfferId(e.target.value ? Number(e.target.value) : null)}
                disabled={loadingRelated || !selectedPartnerId}
                style={fieldSelectStyle}
              >
                <option value="">{selectedPartnerId ? 'None' : 'Select customer first'}</option>
                {offers.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.offer_number} - {o.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Invoice */}
            <div style={{ marginBottom: '16px' }}>
              <div style={fieldLabelStyle}>
                <FaFileInvoiceDollar size={12} color={colors.textMuted} />
                Invoice
              </div>
              <select
                value={selectedInvoiceId || ''}
                onChange={(e) => setSelectedInvoiceId(e.target.value ? Number(e.target.value) : null)}
                disabled={loadingRelated || !selectedPartnerId}
                style={fieldSelectStyle}
              >
                <option value="">{selectedPartnerId ? 'None' : 'Select customer first'}</option>
                {invoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.invoice_number} - {i.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Milestone info badge */}
            {paymentMilestoneId && paymentMilestoneLabel && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  background: colors.successLight,
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: colors.success,
                  border: `1px solid ${colors.successLight}`,
                }}
              >
                Milestone: <strong>{paymentMilestoneLabel}</strong>
              </div>
            )}

            <div style={{ borderTop: `1px solid ${colors.border}`, margin: '16px 0' }} />

            {/* Collaborators */}
            <div style={{ marginBottom: '16px' }}>
              <div style={fieldLabelStyle}>
                <FaUsers size={12} color={colors.textMuted} />
                Collaborators
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {collaborators.length === 0 ? (
                  <span style={{ fontSize: '13px', color: colors.textLight }}>No collaborators</span>
                ) : (
                  collaborators.map((empId) => (
                    <span
                      key={empId}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: colors.infoLight,
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: colors.navy,
                      }}
                    >
                      {getEmployeeName(empId)}
                      <button
                        onClick={() => toggleCollaborator(empId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          color: colors.navy,
                          fontSize: '14px',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
              </div>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    toggleCollaborator(Number(e.target.value));
                  }
                }}
                disabled={loadingRelated}
                style={{ ...fieldSelectStyle, marginTop: '8px' }}
              >
                <option value="">Add collaborator...</option>
                {employees
                  .filter((emp) => !collaborators.includes(emp.id) && emp.id !== assignedTo)
                  .map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        {/* Actions - Always visible */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            padding: '16px 24px',
            borderTop: `1px solid ${colors.border}`,
            background: colors.bgCard,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              ...ps.buttonSecondary,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...(saving ? ps.buttonDisabled : ps.buttonPrimary),
            }}
          >
            {saving ? 'Saving...' : isEditMode ? 'Save changes' : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  );
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: colors.textMuted,
  marginBottom: '12px',
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13px',
  fontWeight: 500,
  color: colors.textMuted,
  marginBottom: '8px',
};

const fieldSelectStyle: React.CSSProperties = {
  ...ps.inputStyle,
  fontSize: '13px',
  cursor: 'pointer',
};
